import React from 'react';
import { 
  MousePointer2, 
  Hand, 
  StickyNote, 
  Type, 
  Square, 
  Circle, 
  ArrowRight,
  MoveHorizontal,
  MoveVertical,
  Spline,
  Pencil,
  Eraser,
  Image,
  Undo2,
  Redo2,
  ZoomIn,
  ZoomOut,
  Maximize2
} from 'lucide-react';
import { Button } from '../ui/button';
import { Separator } from '../ui/separator';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Slider } from '../ui/slider';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../ui/tooltip';

const TOOLS = {
  SELECT: 'select',
  PAN: 'pan',
  STICKY_NOTE: 'sticky_note',
  TEXT: 'text',
  SHAPE_RECT: 'shape_rect',
  SHAPE_CIRCLE: 'shape_circle',
  SHAPE_ARROW: 'shape_arrow',
  CONNECTOR: 'connector',
  CONNECTOR_VERTICAL: 'connector_vertical',
  CONNECTOR_DRAW: 'connector_draw',
  DRAW: 'draw',
  ERASER: 'eraser',
  IMAGE: 'image'
};

const STICKY_COLORS = [
  '#FFEB3B', '#FFC107', '#FF9800', '#FF5722',
  '#E91E63', '#9C27B0', '#673AB7', '#3F51B5',
  '#2196F3', '#03A9F4', '#00BCD4', '#009688',
  '#4CAF50', '#8BC34A', '#CDDC39', '#FFFFFF'
];

const STROKE_COLORS = [
  '#FFFFFF', '#F3F4F6', '#D1D5DB', '#9CA3AF',
  '#EF4444', '#F97316', '#F59E0B', '#EAB308',
  '#84CC16', '#22C55E', '#10B981', '#14B8A6',
  '#06B6D4', '#0EA5E9', '#3B82F6', '#6366F1',
  '#8B5CF6', '#A855F7', '#D946EF', '#EC4899'
];

const TEXT_COLORS = [
  '#FFFFFF', '#F3F4F6', '#E5E7EB', '#D1D5DB',
  '#9CA3AF', '#6B7280', '#374151', '#1F2937',
  '#EF4444', '#F97316', '#F59E0B', '#EAB308',
  '#84CC16', '#22C55E', '#10B981', '#06B6D4',
  '#3B82F6', '#6366F1', '#8B5CF6', '#EC4899'
];

export default function CanvasToolbar({
  activeTool,
  setActiveTool,
  toolOptions,
  setToolOptions,
  onUndo,
  onRedo,
  canUndo,
  canRedo,
  onImageUpload,
  stageScale,
  setStageScale
}) {
  const tools = [
    { id: TOOLS.SELECT, icon: MousePointer2, label: 'Select', shortcut: 'V' },
    { id: TOOLS.PAN, icon: Hand, label: 'Pan', shortcut: 'H' },
    { id: 'separator' },
    { id: TOOLS.STICKY_NOTE, icon: StickyNote, label: 'Sticky Note', shortcut: 'N' },
    { id: TOOLS.TEXT, icon: Type, label: 'Text', shortcut: 'T' },
    { id: 'separator' },
    { id: TOOLS.SHAPE_RECT, icon: Square, label: 'Rectangle', shortcut: 'R' },
    { id: TOOLS.SHAPE_CIRCLE, icon: Circle, label: 'Circle', shortcut: 'C' },
    { id: TOOLS.SHAPE_ARROW, icon: ArrowRight, label: 'Arrow →', shortcut: 'A' },
    { id: TOOLS.CONNECTOR, icon: MoveHorizontal, label: 'Horizontal ↔', shortcut: 'L' },
    { id: TOOLS.CONNECTOR_VERTICAL, icon: MoveVertical, label: 'Vertical ↕', shortcut: 'K' },
    { id: TOOLS.CONNECTOR_DRAW, icon: Spline, label: 'Draw Connection', shortcut: 'J' },
    { id: 'separator' },
    { id: TOOLS.DRAW, icon: Pencil, label: 'Draw', shortcut: 'D' },
    { id: TOOLS.ERASER, icon: Eraser, label: 'Eraser', shortcut: 'E' },
    { id: 'separator' },
    { id: TOOLS.IMAGE, icon: Image, label: 'Image', action: onImageUpload }
  ];

  const handleZoom = (direction) => {
    const newScale = direction === 'in' 
      ? Math.min(stageScale * 1.2, 5)
      : Math.max(stageScale / 1.2, 0.1);
    setStageScale(newScale);
  };

  const handleResetZoom = () => {
    setStageScale(1);
  };

  return (
    <TooltipProvider delayDuration={200}>
      <div className="absolute left-4 top-1/2 -translate-y-1/2 z-40 flex flex-col gap-1 bg-background rounded-lg border shadow-lg p-1.5">
        {tools.map((tool, index) => {
          if (tool.id === 'separator') {
            return <Separator key={`sep-${index}`} className="my-1" />;
          }
          
          const Icon = tool.icon;
          const isActive = activeTool === tool.id;
          
          // Special handling for tools with color options
          if (tool.id === TOOLS.STICKY_NOTE) {
            return (
              <Popover key={tool.id}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <PopoverTrigger asChild>
                      <Button
                        variant={isActive ? 'secondary' : 'ghost'}
                        size="icon"
                        className={`h-9 w-9 ${isActive ? 'bg-primary/10 text-primary' : ''}`}
                        onClick={() => setActiveTool(tool.id)}
                        data-testid={`tool-${tool.id}`}
                      >
                        <Icon className="h-5 w-5" />
                      </Button>
                    </PopoverTrigger>
                  </TooltipTrigger>
                  <TooltipContent side="right">
                    <p>{tool.label} <span className="text-muted-foreground">({tool.shortcut})</span></p>
                  </TooltipContent>
                </Tooltip>
                <PopoverContent side="right" className="w-auto p-2">
                  <p className="text-xs font-medium mb-2">Sticky Note Color</p>
                  <div className="grid grid-cols-4 gap-1">
                    {STICKY_COLORS.map(color => (
                      <button
                        key={color}
                        className={`w-6 h-6 rounded border-2 transition-all ${
                          toolOptions.fillColor === color 
                            ? 'border-primary scale-110' 
                            : 'border-transparent hover:border-gray-300'
                        }`}
                        style={{ backgroundColor: color }}
                        onClick={() => {
                          setToolOptions(prev => ({ ...prev, fillColor: color }));
                          setActiveTool(tool.id);
                        }}
                      />
                    ))}
                  </div>
                </PopoverContent>
              </Popover>
            );
          }
          
          if (tool.id === TOOLS.TEXT) {
            return (
              <Popover key={tool.id}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <PopoverTrigger asChild>
                      <Button
                        variant={isActive ? 'secondary' : 'ghost'}
                        size="icon"
                        className={`h-9 w-9 relative ${isActive ? 'bg-primary/10 text-primary' : ''}`}
                        onClick={() => setActiveTool(tool.id)}
                        data-testid={`tool-${tool.id}`}
                      >
                        <Icon className="h-5 w-5" />
                        {/* Color indicator dot */}
                        <span 
                          className="absolute bottom-1 right-1 w-2 h-2 rounded-full border border-gray-300"
                          style={{ backgroundColor: toolOptions.textColor || '#FFFFFF' }}
                        />
                      </Button>
                    </PopoverTrigger>
                  </TooltipTrigger>
                  <TooltipContent side="right">
                    <p>{tool.label} <span className="text-muted-foreground">({tool.shortcut})</span></p>
                  </TooltipContent>
                </Tooltip>
                <PopoverContent side="right" className="w-48 p-3">
                  <div className="space-y-4">
                    <div>
                      <p className="text-xs font-medium mb-2">Text Color</p>
                      <div className="grid grid-cols-5 gap-1">
                        {TEXT_COLORS.map(color => (
                          <button
                            key={color}
                            className={`w-5 h-5 rounded-full border-2 transition-all ${
                              toolOptions.textColor === color 
                                ? 'border-primary scale-110' 
                                : 'border-transparent hover:border-gray-300'
                            }`}
                            style={{ backgroundColor: color }}
                            onClick={() => setToolOptions(prev => ({ ...prev, textColor: color }))}
                          />
                        ))}
                      </div>
                    </div>
                    <div>
                      <p className="text-xs font-medium mb-2">Font Size: {toolOptions.fontSize || 16}px</p>
                      <Slider
                        value={[toolOptions.fontSize || 16]}
                        onValueChange={([val]) => setToolOptions(prev => ({ ...prev, fontSize: val }))}
                        min={12}
                        max={72}
                        step={2}
                      />
                    </div>
                  </div>
                </PopoverContent>
              </Popover>
            );
          }
          
          if (tool.id === TOOLS.DRAW) {
            return (
              <Popover key={tool.id}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <PopoverTrigger asChild>
                      <Button
                        variant={isActive ? 'secondary' : 'ghost'}
                        size="icon"
                        className={`h-9 w-9 ${isActive ? 'bg-primary/10 text-primary' : ''}`}
                        onClick={() => setActiveTool(tool.id)}
                        data-testid={`tool-${tool.id}`}
                      >
                        <Icon className="h-5 w-5" />
                      </Button>
                    </PopoverTrigger>
                  </TooltipTrigger>
                  <TooltipContent side="right">
                    <p>{tool.label} <span className="text-muted-foreground">({tool.shortcut})</span></p>
                  </TooltipContent>
                </Tooltip>
                <PopoverContent side="right" className="w-48 p-3">
                  <div className="space-y-4">
                    <div>
                      <p className="text-xs font-medium mb-2">Stroke Color</p>
                      <div className="grid grid-cols-5 gap-1">
                        {STROKE_COLORS.map(color => (
                          <button
                            key={color}
                            className={`w-5 h-5 rounded-full border-2 transition-all ${
                              toolOptions.strokeColor === color 
                                ? 'border-primary scale-110' 
                                : 'border-transparent hover:border-gray-300'
                            }`}
                            style={{ backgroundColor: color }}
                            onClick={() => setToolOptions(prev => ({ ...prev, strokeColor: color }))}
                          />
                        ))}
                      </div>
                    </div>
                    <div>
                      <p className="text-xs font-medium mb-2">Stroke Width: {toolOptions.strokeWidth}px</p>
                      <Slider
                        value={[toolOptions.strokeWidth]}
                        onValueChange={([val]) => setToolOptions(prev => ({ ...prev, strokeWidth: val }))}
                        min={1}
                        max={20}
                        step={1}
                      />
                    </div>
                  </div>
                </PopoverContent>
              </Popover>
            );
          }
          
          return (
            <Tooltip key={tool.id}>
              <TooltipTrigger asChild>
                <Button
                  variant={isActive ? 'secondary' : 'ghost'}
                  size="icon"
                  className={`h-9 w-9 ${isActive ? 'bg-primary/10 text-primary' : ''}`}
                  onClick={() => tool.action ? tool.action() : setActiveTool(tool.id)}
                  data-testid={`tool-${tool.id}`}
                >
                  <Icon className="h-5 w-5" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="right">
                <p>{tool.label} {tool.shortcut && <span className="text-muted-foreground">({tool.shortcut})</span>}</p>
              </TooltipContent>
            </Tooltip>
          );
        })}
      </div>
      
      {/* Bottom toolbar - undo/redo and zoom */}
      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-40 flex items-center gap-1 bg-background rounded-lg border shadow-lg p-1.5">
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={onUndo}
              disabled={!canUndo}
            >
              <Undo2 className="h-4 w-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            <p>Undo <span className="text-muted-foreground">(Ctrl+Z)</span></p>
          </TooltipContent>
        </Tooltip>
        
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={onRedo}
              disabled={!canRedo}
            >
              <Redo2 className="h-4 w-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            <p>Redo <span className="text-muted-foreground">(Ctrl+Shift+Z)</span></p>
          </TooltipContent>
        </Tooltip>
        
        <Separator orientation="vertical" className="h-6 mx-1" />
        
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={() => handleZoom('out')}
            >
              <ZoomOut className="h-4 w-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            <p>Zoom Out</p>
          </TooltipContent>
        </Tooltip>
        
        <Button
          variant="ghost"
          size="sm"
          className="h-8 px-2 min-w-[60px] text-xs font-medium"
          onClick={handleResetZoom}
        >
          {Math.round(stageScale * 100)}%
        </Button>
        
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={() => handleZoom('in')}
            >
              <ZoomIn className="h-4 w-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            <p>Zoom In</p>
          </TooltipContent>
        </Tooltip>
        
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={handleResetZoom}
            >
              <Maximize2 className="h-4 w-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            <p>Reset Zoom</p>
          </TooltipContent>
        </Tooltip>
      </div>
    </TooltipProvider>
  );
}
